<template>
  <v-sheet class="custom-calendar-container custom-week-calendar-container fill-height is-relative">
    <div class="d-flex align-center pb-2">
      <zoom-buttons></zoom-buttons>
      <span class="font-size-x-small text--color-neutral-60">
        While dragging/resizing appointments, that appointments's dock availability will display
      </span>
    </div>

    <FullCalendar
      :class="`calendar-view-${$viewType.toLowerCase()}`"
      :data-testid="`calendar-view-${$viewType.toLowerCase()}-container`"
      ref="fullCalendar"
      :options="localCalendarOptions"
      :data-zoom-level="$zoomLevel"
      :data-interval-setting="$intervalMinutes">
      <template #event-content="eventContentInfo">
        <div
          v-if="
            eventContentInfo.event.display !== 'background' &&
            eventContentInfo.event.extendedProps.startTime24
          "
          class="event-content"
          :key="`${renderKey}-${eventContentInfo.event.extendedProps.id}`"
          :data-weekday="eventContentInfo.event.extendedProps.weekday"
          :data-event-id="eventContentInfo.event.id"
          :data-status="eventContentInfo.event.extendedProps.status.toLowerCase()"
          :data-time="eventContentInfo.event.extendedProps.startTime24"
          :data-date="eventContentInfo.event.extendedProps.startDate"
          :data-dock-id="eventContentInfo.event.extendedProps.dockId">
          <event-with-tooltip
            :is-light-theme="$useLightGridTheme"
            :event="{
              // fullcalendar nests any 'non standard' properties under extendedProps
              ...eventContentInfo.event.toPlainObject(),
              ...eventContentInfo.event.extendedProps
            }"
            :enableMilitaryTime="$isMilitaryTimeEnabled($selectedWarehouse)"
            :view-type="CalendarViewEnum.WEEK"
            :use-new-grid-tiles="useNewGridTilesSetting"
            @click="handleEventClick"
            @cancel-reserve="cancelReserve"
            :disable-tooltip="
              isTooltipDisabled(eventContentInfo.event.extendedProps)
            "></event-with-tooltip>
        </div>

        <div
          v-if="
            isStartingStatus(eventContentInfo.event.extendedProps.status) &&
            $rolePermissions.canUpdateAppointment
          "
          class="v-event-drag-bottom"
          @mousedown.stop="extendBottom(eventContentInfo.event.extendedProps)"></div>
      </template>

      <template #day-header-content="{ date, text }">
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <a v-bind="props" href="#" @click="handleCalendarClick({ date })">{{ text }}</a>
          </template>
          <span v-if="$areEventsReady">{{ totalAppointmentsByDayString(date) }}</span>
        </v-tooltip>
      </template>
    </FullCalendar>

    <calendar-dialogs
      :appointment-context="appointmentContext"
      :show-create-dialog="showCreateDialog"
      :show-details-dialog="showDetailsDialog"
      :show-reserve-dialog="showReserveDialog"
      @close="handleDialogClose" />

    <event-context-menu
      v-if="eventContextMenuEvent"
      @close="clearContextMenuData"
      :nativeEvent="eventContextMenuEvent"
      :appointment="{
        ...localEvents.find(event => event.id === eventContextMenuId)
      }" />
  </v-sheet>
</template>

<script>
import calendarMixin from '@/modules/calendar/mixins/calendarMixin';
import { CalendarViewEnum } from '@/enums';
import FullCalendar from '@fullcalendar/vue3';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon2';
import scrollGrid from '@fullcalendar/scrollgrid';
import { DateTime, Info, Interval } from 'luxon';
import { padStart } from 'lodash';
import { isRescheduleAllowed } from '@nova/core';

/**
 * Calendar Day View
 * @displayName Calendar Day View
 */
export default {
  name: 'CalendarDayView',
  mixins: [calendarMixin],
  components: {
    FullCalendar
  },
  computed: {
    localCalendarOptions() {
      return {
        ...this.calendarOptions,
        contentHeight: 'auto',
        dayMinWidth: 100,
        editable: true,
        businessHours: this.warehouseBusinessHours,

        // These two enables the date header click function
        navLinks: true,
        navLinkDayClick: (date, jsEvent) => {
          this.handleCalendarClick({ date, jsEvent });
        },

        eventDragStart: info => {
          // This will show the dock AND warehouse hours while dragging so it's more user-friendly
          this.getWarehouseBusinessHours(info.event.extendedProps.dockId);
        },
        eventDragStop: () => {
          // If more than one dock and the target is inside the grid, we need to get business hours
          // without the current event's dock hours
          if (this.docks.length > 1) {
            this.getWarehouseBusinessHours();
          }
        },
        eventResizeStart: info => {
          // This will show the dock AND warehouse hours while resizing so it's more user-friendly
          if (this.docks.length > 1) {
            this.getWarehouseBusinessHours(info.event.extendedProps.dockId);
          }
        },
        eventResizeStop: () => {
          // This will show the dock AND warehouse hours while resizing so it's more user-friendly
          if (this.docks.length > 1) {
            this.getWarehouseBusinessHours();
          }
        },
        events: this.localEvents,
        firstDay: 0,
        initialView: 'timeGridWeek',
        plugins: [luxonPlugin, timeGridPlugin, interactionPlugin, scrollGrid],
        viewDidMount: () => {
          this.addCustomClassesToSlotLabels();
          this.ready = true;
        },
        eventDidMount: this.handleEventDidMount
      };
    },
    localEvents() {
      const localEvents = this.events.map(event => {
        // Editing rules for drag/drop and duration
        if (isRescheduleAllowed(event.status)) {
          event.durationEditable = true;
          event.startEditable = true;
        } else {
          event.startEditable = false;
          event.durationEditable = false;
        }
        return event;
      });

      return localEvents;
    }
  },
  data() {
    return {
      warehouseBusinessHours: []
    };
  },
  methods: {
    totalAppointmentsByDay(dateTime) {
      if (dateTime) {
        return (
          this.events?.filter(event => {
            const notReserve = !this.novaCore.isReserve(event);
            const notCanceled = event?.status !== this.novaCore.AppointmentStatus.Cancelled;
            const dayAppointment = DateTime.fromISO(event.start)
              .setZone(this.timezone)
              .toISO()
              .startsWith(dateTime.toFormat('yyyy-MM-dd'));

            return notReserve && notCanceled && dayAppointment;
          }).length || 0
        );
      }

      return 0;
    },
    totalAppointmentsByDayString(jsDate) {
      const dateTime = DateTime.fromJSDate(jsDate, {
        zone: this.timezone,
        setZone: true
      });
      const total = this.totalAppointmentsByDay(dateTime);
      return `${total} appointment`.concat(total === 1 ? '' : 's');
    },
    handleEventAllow(info, event) {
      const isResize = event.startStr === info.startStr && event.endStr !== info.endStr;
      const isValidSnapTime = isResize && this.snapOverride ? true : this.isSnapTime(info.startStr);
      if (!isValidSnapTime) {
        return false;
      }

      return true;
    },
    handleCalendarClick(dateClickInfo) {
      const dateTimeUTC = DateTime.fromJSDate(dateClickInfo.date, {
        zone: 'utc',
        setZone: true
      });
      if (this.docks.length > 1) {
        this.$store.dispatch('Calendar/setSelectedDate', {
          value: dateTimeUTC.toISO(),
          getEvents: true
        });

        this.$store.dispatch('Calendar/setViewType', { value: CalendarViewEnum.DAY.toUpperCase() });
      } else {
        const clickTarget = dateClickInfo.jsEvent.target;
        const classNamesToCheck = ['mdi', 'fc-non-business'];
        if (classNamesToCheck.some(className => clickTarget.classList.contains(className))) {
          return;
        }
        if (!this.$selectedWarehouse?.id) {
          this.$eventHub.$emit('shake-element', 'warehouse-select');
          return;
        }

        let dock = this.$docksKeyedById[this.docks[0].id];

        if (dock) {
          if (this.novaCore.isCapacityChild(dock)) {
            dock = this.$docksKeyedById[dock.capacityParentId];
          }
          this.showCreateAppointmentDialog(dateClickInfo, dock);
        } else {
          this.$eventHub.$emit('shake-element', 'dock-select');
        }
      }
    },
    getWarehouseBusinessHours(dockId = null) {
      const schedule = this.$selectedWarehouse?.schedule ?? this.novaCore.getFullyOpenSchedule();

      // e.g. ["monday", "tuesday", ...]
      const weekdays = Info.weekdays().map(w => w.toLowerCase());

      // Figure out which 7-day period we’re dealing with
      const startDateTime = DateTime.fromISO(this.$selectedDate, {
        zone: this.timezone,
        setZone: true
      });

      // Subtract a day because Luxon starts weeks on Monday
      const startOfWeek = startDateTime.startOf('week').minus({ days: 1 });
      const dateRangeInterval = Interval.fromDateTimes(
        startOfWeek,
        startDateTime.plus({ days: 7 })
      );

      // Build a Set of closed days for the warehouse (e.g. "2025-01-02")
      const closedDayKeys = new Set();
      for (const interval of schedule?.closedIntervals || []) {
        const dateTime = DateTime.fromISO(interval.start, { zone: this.timezone });
        if (dateRangeInterval.contains(dateTime)) {
          closedDayKeys.add(dateTime.toFormat('yyyy-MM-dd'));
        }
      }

      // Optionally, also build a Set of closed days for the dock
      let dockClosedDayKeys = new Set();
      let dockSchedule = null;
      if (dockId && this.docks?.length) {
        dockSchedule = this.$docksKeyedById[dockId].schedule;
        dockClosedDayKeys = new Set();
        for (const interval of dockSchedule?.closedIntervals || []) {
          const dateTime = DateTime.fromISO(interval.start, { zone: this.timezone });
          if (dateRangeInterval.contains(dateTime)) {
            dockClosedDayKeys.add(dateTime.toFormat('yyyy-MM-dd'));
          }
        }
      }

      // Precompute the actual DateTimes for each day from our startOfWeek
      // Array of [ startOfWeek + 0 days, startOfWeek + 1 day, ... , +6 days ]
      const dayDateTimes = Array.from({ length: 7 }, (_, i) => startOfWeek.plus({ days: i }));

      // Filter out keys we don't need
      const validDays = Object.keys(schedule)?.filter(
        day => !['closedIntervals', 'version'].includes(day)
      );

      const businessHours = [];

      // Loop over valid day keys in the schedule
      for (const day of validDays) {
        const warehouseDaySchedule = schedule[day] || [];
        // Figure out the numeric weekday from the array we built above
        const weekdayNumber = weekdays.findIndex(w => w === day) + 1;
        // Sunday in FullCalendar is 0, so we shift if needed
        const adjustedWeekdayNumber = weekdayNumber === 7 ? 0 : weekdayNumber;

        // The DateTime for this day (Mon=1 => index 1 in array, etc.)
        const dayDateTime = dayDateTimes[adjustedWeekdayNumber];
        // e.g. "2023-01-02"
        const dayKey = dayDateTime.toFormat('yyyy-MM-dd');

        // Skip if the warehouse is closed for that day
        if (closedDayKeys.has(dayKey)) {
          continue;
        }

        // If including dock hours, check if dock is closed too
        if (dockId && dockSchedule) {
          if (dockClosedDayKeys.has(dayKey)) {
            continue;
          }
          // Merge warehouse & dock schedules for this day
          const dockDaySchedule = dockSchedule[day] || [];
          const mergedIntervals = this.mergeWarehouseAndDockDaySchedule(
            warehouseDaySchedule,
            dockDaySchedule
          );

          for (const interval of mergedIntervals) {
            let endTime = interval.end;
            if (endTime === '23:59') {
              endTime = '24:00';
            }
            businessHours.push({
              daysOfWeek: [adjustedWeekdayNumber],
              startTime: padStart(interval.start, 5, '0'),
              endTime: padStart(endTime, 5, '0')
            });
          }
        } else {
          // Just use the warehouse schedule
          for (const interval of warehouseDaySchedule) {
            let endTime = interval.end;
            if (endTime === '23:59') {
              endTime = '24:00';
            }
            businessHours.push({
              daysOfWeek: [adjustedWeekdayNumber],
              startTime: padStart(interval.start, 5, '0'),
              endTime: padStart(endTime, 5, '0')
            });
          }
        }
      }

      // Store and return
      this.warehouseBusinessHours = businessHours.length ? businessHours : { daysOfWeek: [] };
      return this.warehouseBusinessHours;
    }
  },
  async mounted() {
    this.calendarApi.gotoDate(this.$selectedDate);
    this.mounted = true;
  },
  watch: {
    docks: {
      handler(newVal) {
        const dockId = newVal.length === 1 ? newVal[0].id : null;
        this.getWarehouseBusinessHours(dockId);
      },
      immediate: true
    },
    $selectedDate() {
      if (this.mounted) {
        this.calendarApi.gotoDate(this.$selectedDate);
        const dockId = this.docks.length === 1 ? this.docks[0].id : null;
        this.getWarehouseBusinessHours(dockId);
      }
    },
    $selectedWarehouse() {
      this.getWarehouseBusinessHours();
      this.shouldShowCapacityDockOpts = {};
    }
  }
};
</script>
